import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractDynamicComponent } from '@components/abstracts';
import { TitreFiltresModeles } from '@components/titre-filtres-modeles/models';
import { TranslatePipe } from '@ngx-translate/core';
import { FilterSelectComponent } from '@shared/components/filter-select';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { Option } from '@shared/models';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { CurrentLanguageService, LanguageService } from '@shared/services/language';
import { ResponsiveService } from '@shared/services/responsive';
import { UrlParamsService } from '@shared/services/url-params';
import { formatAsWholeNumber } from '@shared/utils';

@Component({
  selector: 'app-titre-filtres-modeles',
  imports: [TitleComponent, SvgComponent, TranslatePipe, NgClass, NgTemplateOutlet, FilterSelectComponent],
  templateUrl: './titre-filtres-modeles.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TitreFiltresModelesComponent extends AbstractDynamicComponent<TitreFiltresModeles> {
  readonly breakpoints = inject(ResponsiveService).breakpoints;
  hasFiltersOpen = signal(false);
  readonly filterBudgetOptions: WritableSignal<Option[]> = signal([]);
  private readonly elasticSearchService = inject(ElasticSearchService);
  readonly filterImplantationOptions = toSignal(this.elasticSearchService.getLayoutFilter());
  readonly filterColorsOptions = toSignal(this.elasticSearchService.getColorsFilter());
  private readonly urlParamsService = inject(UrlParamsService);
  private readonly params = this.urlParamsService.params;
  readonly canViewResetFiltersButton = computed(() => {
    const { filter_layout, filter_budget, filter_front } = this.params();
    return !!(filter_layout ?? filter_budget ?? filter_front);
  });
  private readonly languageService = inject(LanguageService);
  private readonly language = inject(CurrentLanguageService).language;
  readonly filterSelectionOptions: Option[] = [
    {
      key: 'our_selection',
      label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.OUR_SELECTION'),
      isSelected: true,
    },
    {
      key: 'price_ascending',
      label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_ASCENDING'),
    },
    {
      key: 'price_descending',
      label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_DESCENDING'),
    },
  ];

  constructor() {
    super();
    afterNextRender(() => {
      this.filterBudgetOptions.set(this.buildFilterBudgetOptions());
    });
    effect(() => {
      const { filter_layout, filter_budget, filter_front } = this.params();
      if (filter_layout || filter_budget || filter_front) {
        this.hasFiltersOpen.set(true);
      }
    });
  }

  emitGtmEventWrapper() {
    const gtm = this.data.gtmConfig;
    this.emitGtmEvent({
      event: gtm?.event,
      group: gtm?.group,
      description: gtm?.description,
      context: gtm?.context,
      event_type: 'click',
    });
  }

  resetFilter(): void {
    this.urlParamsService.removeParams('filter_layout', 'filter_budget', 'filter_front', 'listing_page');
  }

  /**
   * Builds an array of filter options based on the provided budget data.
   * The method processes a semicolon-separated budget string and categorizes the data into valid options
   * such as explicit ranges (e.g., "2000-4000"), single numbers with contextual descriptions
   * (e.g., "Less than 2000", "More than 4000"), or other fallback formats for invalid or malformed entries.
   *
   * Each option includes a translated label and corresponding key value for usage in the application.
   * Translation strings are used to format contextual descriptions (e.g., `PRICE_LESS_THAN`, `PRICE_MORE_THAN`, `PRICE_RANGE`).
   *
   * @return {Option[]} An array of formatted budget filter options. Each option is an object with:
   * - `label`: A user-friendly, translated description of the budget option.
   * - `key`: A string representing the raw or processed budget value for identification.
   * The returned array is empty if there is no available or valid budget data.
   */
  private buildFilterBudgetOptions(): Option[] {
    // Retrieve the raw budget string from data.
    const rawBudget = this.data?.budget;

    // If there's no budget data, return an empty array.
    if (!rawBudget) {
      return [];
    }

    // Process the raw string into a clean array of entries.
    const entries = rawBudget
      .split(';') // Split by semicolon.
      .map((entry) => entry.trim()) // Remove whitespace.
      .filter((entry) => entry.length > 0); // Remove empty entries.

    const options: Option[] = [];

    // Iterate over the entries to apply formatting rules based on position and value.
    // Iterate over the entries to apply formatting rules based on position and value.
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];

      if (!entry) {
        continue;
      }

      // Case 1: The entry is an explicit range (e.g., "2000-4000").
      if (entry.includes('-')) {
        const [minStr, maxStr] = entry.split('-');
        const min = Number(minStr);
        const max = Number(maxStr);

        // Check if the range values are valid numbers.
        if (!isNaN(min) && !isNaN(max)) {
          options.push({
            label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_RANGE', {
              min: formatAsWholeNumber(min, this.language.locale),
              max: formatAsWholeNumber(max, this.language.locale),
            }),
            key: entry,
          });
        } else {
          // Fallback for a malformed range.
          options.push({ label: entry, key: entry });
        }
      } else {
        // Case 2: The entry is a single number.
        const amount = Number(entry);

        // Check if the value is a valid number.
        if (isNaN(amount)) {
          // Fallback for a malformed number entry.
          options.push({ label: entry, key: entry });
          continue; // Skip to the next entry.
        }

        const isFirst = i === 0;
        const isLast = i === entries.length - 1;

        // Apply rules based on the entry's position in the array.
        if (isFirst) {
          // Rule: If it's the first entry, format as "Less than X".
          // Assumes the existence of a 'PRICE_LESS_THAN' translation key.
          options.push({
            label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_LESS_THAN', {
              amount: formatAsWholeNumber(amount, this.language.locale),
            }),
            key: `0-${entry}`,
          });
        } else if (isLast) {
          // Rule: If it's the last entry, format as "More than X".
          options.push({
            label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_MORE_THAN', {
              amount: formatAsWholeNumber(amount, this.language.locale),
            }),
            key: entry,
          });
        } else {
          // Rule: If it's a middle entry, create a range with the next entry.
          const nextEntry = entries[i + 1];

          if (nextEntry) {
            // The next entry's start value is the first part before a potential dash.
            const nextAmountStr = nextEntry.split('-')[0];
            const nextAmount = Number(nextAmountStr);

            if (!isNaN(nextAmount)) {
              options.push({
                label: this.languageService.instant('COMPONENT.TITRE-FILTRES-MODELES.PRICE_RANGE', {
                  min: formatAsWholeNumber(amount, this.language.locale),
                  max: formatAsWholeNumber(nextAmount, this.language.locale),
                }),
                // The key represents the newly created range.
                key: `${amount}-${nextAmount}`,
              });
            } else {
              // Fallback if the next entry is not a valid number.
              options.push({ label: entry, key: entry });
            }
          } else {
            // Fallback if the next entry is not available.
            options.push({ label: entry, key: entry });
          }
        }
      }
    }

    return options;
  }
}
